
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Table from "../../components/Table";
import AddDialog from "../../components/Modal/AddModal";
import SearchableSelect from "../../components/Select/SearchableSelect";
import SelectInput from "../../components/Select/SelectInput";
import TextInput from "../../components/Select/InputText";
import { useAuth } from "../../context/AuthContext";
import { getCoachesService } from "../../services/coachService";
import { getMembers } from "../../services/membersService";
import { createSessionService, deleteSessionService, getSessionsService, updateSessionPaymentService } from "../../services/sessionService";
import { showErrorToast, showSuccessToast } from "../../services/toastService";
import { hasPermission } from "../../utils/permissions";
import { FaTrash } from "react-icons/fa";

type SessionRow = {
    _id: string;
    coachId: string;
    coachName: string;
    memberName: string;
    date: string;
    time: string;
    notes: string;
    billingType: "package" | "pay_per_use";
    clientPrice: number;
    paymentStatus: "not_applicable" | "paid" | "unpaid";
};

type OptionItem = {
    value: string;
    label: string;
};

const PAGE_SIZE = 10;

const timeOptions: OptionItem[] = Array.from({ length: 48 }, (_, index) => {
    const hour = Math.floor(index / 2);
    const minutes = index % 2 === 0 ? "00" : "30";
    const value = `${String(hour).padStart(2, "0")}:${minutes}`;
    return {
        value,
        label: `${hour}:${minutes}`,
    };
});

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const asString = (value: unknown): string => (typeof value === "string" ? value : "");

const toArray = (value: unknown): unknown[] => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") {
        const data = value as { data?: unknown; items?: unknown; coaches?: unknown; members?: unknown };
        if (Array.isArray(data.data)) return data.data;
        if (Array.isArray(data.items)) return data.items;
        if (Array.isArray(data.coaches)) return data.coaches;
        if (Array.isArray(data.members)) return data.members;
    }
    return [];
};

const buildName = (entry: Record<string, unknown>): string => {
    const first = asString(entry.firstName);
    const last = asString(entry.lastName);
    const full = `${first} ${last}`.trim();
    return full || asString(entry.name) || "-";
};

const normalizeDate = (value: unknown): string => {
    const raw = asString(value);
    if (!raw) return "-";
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    return date.toISOString().split("T")[0];
};

const normalizeTime = (value: unknown): string => {
    const raw = asString(value);
    if (!raw) return "-";
    if (raw.includes("T")) {
        const date = new Date(raw);
        if (!Number.isNaN(date.getTime())) {
            return date.toTimeString().slice(0, 5);
        }
    }
    return raw.slice(0, 5);
};

const formatMoney = (value: number): string => (
    Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
);

const readErrorMessage = (err: unknown, fallback: string): string => {
    if (!err || typeof err !== "object") return fallback;
    const errObj = err as { response?: { data?: { message?: string } } };
    return errObj.response?.data?.message ?? fallback;
};

const SessionsPage = () => {
    const { user } = useAuth();
    const canEditSessions = hasPermission(user, "sessions.write");
    const canViewAssign = hasPermission(user, "assign.view");

    const [rows, setRows] = useState<SessionRow[]>([]);
    const [coaches, setCoaches] = useState<OptionItem[]>([]);
    const [members, setMembers] = useState<OptionItem[]>([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [coachFilter, setCoachFilter] = useState("");
    const [startDateFilter, setStartDateFilter] = useState("");
    const [endDateFilter, setEndDateFilter] = useState("");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [updatingPaymentId, setUpdatingPaymentId] = useState("");

    const fetchSessions = useCallback(async () => {
        if (!user?.id) return;

        setError("");
        setLoading(true);
        try {
            const response = await getSessionsService({
                page,
                limit: PAGE_SIZE,
                coachId: coachFilter,
                startDate: startDateFilter,
                endDate: endDateFilter,
            });
            const normalized: SessionRow[] = response.rows.map((entry) => {
                const session = asRecord(entry);
                const coach = asRecord(session.coach || session.coachId);
                const member = asRecord(session.member || session.memberId);

                const coachId = asString(session.coachId) || asString(coach._id) || asString(coach.id);

                return {
                    _id: asString(session._id) || asString(session.id) || crypto.randomUUID(),
                    coachId,
                    coachName: buildName(coach) || asString(asRecord(session.coachId).firstName) || asString(session.coachName) || "-",
                    memberName: buildName(member) || asString(asRecord(session.memberId).firstName) || asString(session.memberName) || "-",
                    date: normalizeDate(session.date || session.sessionDate || session.createdAt),
                    time: normalizeTime(session.time || session.sessionTime || session.createdAt),
                    notes: asString(session.notes),
                    billingType: asString(session.billingType) === "pay_per_use" ? "pay_per_use" : "package",
                    clientPrice: Number(session.clientPrice || 0),
                    paymentStatus: asString(session.paymentStatus) === "paid"
                        ? "paid"
                        : asString(session.paymentStatus) === "unpaid" ? "unpaid" : "not_applicable",
                };
            });
            setRows(normalized);
            setTotal(response.total);
        } catch (err: unknown) {
            console.error("Failed to fetch sessions", err);
            setError(readErrorMessage(err, "Failed to load sessions"));
        } finally {
            setLoading(false);
        }
    }, [user?.id, user?._id, page, coachFilter, startDateFilter, endDateFilter]);

    const fetchOptions = useCallback(async () => {
        if (!user?.id) return;

        try {
            const coachesResponse = await getCoachesService();

            const coachOptions = toArray(coachesResponse)
                .map((entry) => {
                    const coach = asRecord(entry);
                    return {
                        value: asString(coach._id) || asString(coach.id),
                        label: buildName(coach),
                    };
                })
                .filter((item) => item.value);

            setCoaches(coachOptions);
        } catch (err) {
            console.error("Failed to load coaches", err);
            setError("Failed to load coach options");
        }
    }, [user?.id]);

    const searchMembers = useCallback(async (query: string) => {
        const trimmedQuery = query.trim();
        if (trimmedQuery.length < 3) {
            setMembers([]);
            return;
        }

        setLoadingMembers(true);
        try {
            const membersResponse = await getMembers({ page: 1, limit: 30, search: trimmedQuery });
            const memberOptions = toArray(membersResponse)
                .map((entry) => {
                    const member = asRecord(entry);
                    return {
                        value: asString(member._id) || asString(member.id),
                        label: buildName(member),
                    };
                })
                .filter((item) => item.value);

            setMembers(memberOptions);
        } catch (err) {
            console.error("Failed to search members", err);
            setMembers([]);
            setError("Failed to search members");
        } finally {
            setLoadingMembers(false);
        }
    }, []);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    useEffect(() => {
        fetchOptions();
    }, [fetchOptions]);

    const openDialog = async () => {
        setAddOpen(true);
        setMembers([]);
        await fetchOptions();
    };

    const formik = useFormik({
        initialValues: {
            coachId: "",
            memberId: "",
            date: "",
            time: "",
            notes: "",
            billingType: "package" as "package" | "pay_per_use",
            clientPrice: "",
            paymentStatus: "paid" as "paid" | "unpaid",
        },
        validationSchema: Yup.object({
            coachId: Yup.string().required("Coach is required"),
            memberId: Yup.string().required("Member is required"),
            date: Yup.string().required("Date is required"),
            time: Yup.string().required("Time is required"),
            notes: Yup.string(),
            billingType: Yup.string().oneOf(["package", "pay_per_use"]).required("Billing type is required"),
            clientPrice: Yup.number().when("billingType", {
                is: "pay_per_use",
                then: (schema) => schema
                    .typeError("Client price is required")
                    .moreThan(0, "Client price must be greater than 0")
                    .required("Client price is required"),
                otherwise: (schema) => schema
                    .transform((value, originalValue) => originalValue === "" ? undefined : value)
                    .nullable(),
            }),
            paymentStatus: Yup.string().when("billingType", {
                is: "pay_per_use",
                then: (schema) => schema.oneOf(["paid", "unpaid"]).required("Payment status is required"),
                otherwise: (schema) => schema,
            }),
        }),
        onSubmit: async (values, { resetForm }) => {
            if (!user?.id) {
                showErrorToast("User not logged in");
                return;
            }

            try {
                await createSessionService({
                    coachId: values.coachId,
                    memberId: values.memberId,
                    date: values.date,
                    time: values.time,
                    notes: values.notes || undefined,
                    billingType: values.billingType,
                    ...(values.billingType === "pay_per_use" ? {
                        clientPrice: Number(values.clientPrice),
                        paymentStatus: values.paymentStatus,
                    } : {}),
                });

                await fetchSessions();
                resetForm();
                setAddOpen(false);
            } catch (err: unknown) {
                console.error("Failed to create session", err);
                showErrorToast(readErrorMessage(err, "Failed to create session"));
            }
        },
    });

    const closeDialog = () => {
        setAddOpen(false);
        formik.resetForm();
    };

    const handleDeleteSession = async (session: SessionRow) => {
        if (!session?._id) {
            showErrorToast("Session ID is missing");
            return;
        }

        const label = [session.memberName, session.date].filter(Boolean).join(" on ");
        if (!window.confirm(`Delete session${label ? ` for ${label}` : ""}?`)) return;

        try {
            await deleteSessionService(session._id);
            showSuccessToast("Session deleted");
            await fetchSessions();
        } catch (err: unknown) {
            console.error("Failed to delete session", err);
            showErrorToast(readErrorMessage(err, "Failed to delete session"));
        }
    };

    const handlePaymentStatus = async (session: SessionRow) => {
        if (session.billingType !== "pay_per_use") return;
        const nextStatus = session.paymentStatus === "paid" ? "unpaid" : "paid";

        try {
            setUpdatingPaymentId(session._id);
            await updateSessionPaymentService(session._id, nextStatus);
            showSuccessToast(nextStatus === "paid" ? "Session marked as paid" : "Session marked as unpaid");
            await fetchSessions();
        } catch (err: unknown) {
            console.error("Failed to update session payment", err);
            showErrorToast(readErrorMessage(err, "Failed to update payment status"));
        } finally {
            setUpdatingPaymentId("");
        }
    };

    const columns = useMemo(
        () => [
            { Header: "Coach", accessor: "coachName" },
            { Header: "Member", accessor: "memberName" },
            { Header: "Date", accessor: "date" },
            { Header: "Time", accessor: "time" },
            {
                Header: "Billing",
                accessor: "billingType",
                Cell: ({ row }: { row: { original: SessionRow } }) => (
                    row.original.billingType === "pay_per_use" ? "Pay Per Use" : "Package"
                ),
            },
            {
                Header: "Client Price",
                accessor: "clientPrice",
                Cell: ({ row }: { row: { original: SessionRow } }) => (
                    row.original.billingType === "pay_per_use" ? formatMoney(row.original.clientPrice) : "-"
                ),
            },
            {
                Header: "Payment",
                accessor: "paymentStatus",
                Cell: ({ row }: { row: { original: SessionRow } }) => {
                    if (row.original.billingType !== "pay_per_use") return "-";
                    const paid = row.original.paymentStatus === "paid";
                    return (
                        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${paid ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                            {paid ? "Paid" : "Unpaid"}
                        </span>
                    );
                },
            },
            { Header: "Notes", accessor: "notes" },
            ...(canEditSessions
                ? [
                    {
                        Header: "Actions",
                        accessor: "actions",
                        Cell: ({ row }: { row: { original: SessionRow } }) => (
                            <div className="flex items-center gap-3">
                                {row.original.billingType === "pay_per_use" && (
                                    <button
                                        type="button"
                                        onClick={() => handlePaymentStatus(row.original)}
                                        disabled={updatingPaymentId === row.original._id}
                                        className="whitespace-nowrap text-xs font-semibold text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                    >
                                        {updatingPaymentId === row.original._id
                                            ? "Updating..."
                                            : row.original.paymentStatus === "paid" ? "Mark Unpaid" : "Mark Paid"}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => handleDeleteSession(row.original)}
                                    className="text-red-500 hover:text-red-700"
                                    aria-label="Delete session"
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        ),
                    },
                ]
                : []),
        ],
        [canEditSessions, handleDeleteSession, updatingPaymentId]
    );

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="p-6 w-full">
            <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Sessions</h1>
                    <p className="text-sm text-gray-500">Create package or pay-per-use PT sessions between coach and member</p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    {canViewAssign && (
                        <Link
                            to="/assign"
                            className="rounded-md border border-gray-300 px-4 py-2 text-center text-sm font-semibold hover:bg-gray-100"
                        >
                            Assign
                        </Link>
                    )}
                    <Link
                        to="/privatesubscriptions"
                        className="rounded-md bg-[#ac120c] px-4 py-2 text-center text-sm font-semibold text-white hover:bg-[#90100b]"
                    >
                        Subscriptions
                    </Link>
                </div>
            </div>

            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

            <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4">
                <div className="flex flex-col gap-1">
                    <label className="text-sm font-semibold">Filter by Coach</label>
                    <select
                        name="coachFilter"
                        value={coachFilter}
                        onChange={(event) => {
                            setCoachFilter(event.target.value);
                            setPage(1);
                        }}
                        className="rounded border px-3 py-2"
                    >
                        <option value="">All Coaches</option>
                        {coaches.map((coach) => (
                            <option key={coach.value} value={coach.value}>
                                {coach.label}
                            </option>
                        ))}
                    </select>
                </div>
                <TextInput
                    label="Start Date"
                    type="date"
                    name="startDateFilter"
                    value={startDateFilter}
                    onChange={(event) => {
                        setStartDateFilter(event.target.value);
                        setPage(1);
                    }}
                />
                <TextInput
                    label="End Date"
                    type="date"
                    name="endDateFilter"
                    value={endDateFilter}
                    onChange={(event) => {
                        setEndDateFilter(event.target.value);
                        setPage(1);
                    }}
                />
                <div className="flex items-end">
                    <button
                        type="button"
                        onClick={() => {
                            setCoachFilter("");
                            setStartDateFilter("");
                            setEndDateFilter("");
                            setPage(1);
                        }}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-100"
                    >
                        Clear Filters
                    </button>
                </div>
            </div>

            <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-semibold text-gray-700">
                    Total Sessions: {loading ? "..." : total.toLocaleString()}
                </p>
                {loading && <p className="text-sm text-gray-500">Loading sessions...</p>}
            </div>

            <Table
                columns={columns}
                data={rows}
                pageSize={PAGE_SIZE}
                add={canEditSessions}
                onOpen={openDialog}
                showSearch={false}
                showPagination={false}
            />

            <div className="mt-4 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={loading || page <= 1}
                    className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50"
                >
                    Previous
                </button>
                <span className="text-sm">
                    Page {page} of {totalPages}
                </span>
                <button
                    type="button"
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={loading || page >= totalPages}
                    className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50"
                >
                    Next
                </button>
            </div>

            <AddDialog title="Create PT Session" isOpen={addOpen} onClose={closeDialog} maxWidth="max-w-2xl">
                <form onSubmit={formik.handleSubmit} className="space-y-4">
                    <SelectInput
                        label="Coach"
                        name="coachId"
                        value={formik.values.coachId}
                        onChange={formik.handleChange}
                        options={coaches}
                    />
                    {formik.touched.coachId && formik.errors.coachId && (
                        <p className="text-red-500 text-sm">{formik.errors.coachId}</p>
                    )}

                    <SearchableSelect
                        label="Member"
                        name="memberId"
                        value={formik.values.memberId}
                        options={members}
                        placeholder="Search members..."
                        minSearchLength={3}
                        loading={loadingMembers}
                        loadingText="Searching members..."
                        noOptionsText="No members found"
                        onChange={(name, selectedValue) => formik.setFieldValue(name, selectedValue)}
                        onBlur={(name) => formik.setFieldTouched(name, true)}
                        onSearch={searchMembers}
                    />
                    {formik.touched.memberId && formik.errors.memberId && (
                        <p className="text-red-500 text-sm">{formik.errors.memberId}</p>
                    )}

                    <SelectInput
                        label="Billing Type"
                        name="billingType"
                        value={formik.values.billingType}
                        onChange={(event) => {
                            formik.handleChange(event);
                            if (event.target.value === "package") {
                                formik.setFieldValue("clientPrice", "");
                                formik.setFieldValue("paymentStatus", "paid");
                            }
                        }}
                        options={[
                            { value: "package", label: "Package Session" },
                            { value: "pay_per_use", label: "Pay Per Use" },
                        ]}
                    />
                    {formik.touched.billingType && formik.errors.billingType && (
                        <p className="text-red-500 text-sm">{formik.errors.billingType}</p>
                    )}

                    {formik.values.billingType === "pay_per_use" && (
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <TextInput
                                    label="Client Price"
                                    type="number"
                                    name="clientPrice"
                                    min="0.01"
                                    step="0.01"
                                    value={formik.values.clientPrice}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="0.00"
                                />
                                {formik.touched.clientPrice && formik.errors.clientPrice && (
                                    <p className="text-red-500 text-sm">{formik.errors.clientPrice}</p>
                                )}
                            </div>
                            <SelectInput
                                label="Payment Status"
                                name="paymentStatus"
                                value={formik.values.paymentStatus}
                                onChange={formik.handleChange}
                                options={[
                                    { value: "paid", label: "Paid" },
                                    { value: "unpaid", label: "Unpaid" },
                                ]}
                            />
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <TextInput
                                label="Date"
                                type="date"
                                name="date"
                                value={formik.values.date}
                                onChange={formik.handleChange}
                            />
                            {formik.touched.date && formik.errors.date && (
                                <p className="text-red-500 text-sm">{formik.errors.date}</p>
                            )}
                        </div>
                        <div>
                            <SelectInput
                                label="Time"
                                name="time"
                                value={formik.values.time}
                                onChange={formik.handleChange}
                                options={timeOptions}
                            />
                            {formik.touched.time && formik.errors.time && (
                                <p className="text-red-500 text-sm">{formik.errors.time}</p>
                            )}
                        </div>
                    </div>

                    <TextInput
                        label="Notes (optional)"
                        type="text"
                        name="notes"
                        value={formik.values.notes}
                        onChange={formik.handleChange}
                        placeholder="Session notes"
                    />

                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" onClick={closeDialog} className="px-4 py-2 border border-gray-300 rounded">
                            Cancel
                        </button>
                        <button type="submit" className="px-4 py-2 bg-[#ac120c] text-white rounded">
                            Create Session
                        </button>
                    </div>
                </form>
            </AddDialog>
        </div>
    );
};

export default SessionsPage;
